import { AgropilotFeature } from '@agroone/entities'
import { Directive, TemplateRef, ViewContainerRef, computed, effect, inject, input } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { AgropilotPermissionManagerService } from '../services/agropilot-permission-manager.service'
import { SharedUserService } from '../services/shared-user.service'

type ExpectedPermissions = { [feature: string]: 'read' | 'write' }

const toExpectedPermissions = (
  value: ExpectedPermissions | string[] | string | AgropilotFeature,
): ExpectedPermissions => {
  if (typeof value === 'string') {
    return { [value]: 'read' }
  }
  if (Array.isArray(value)) {
    return value.reduce<ExpectedPermissions>((prev, curr) => {
      prev[curr] = 'read'
      return prev
    }, {})
  }

  return value
}

@Directive({
  selector: '[hasAgropilotPermission]',
})
export class HasAgropilotPermissionDirective {
  private templateRef = inject<TemplateRef<unknown>>(TemplateRef)
  private viewContainer = inject(ViewContainerRef)
  private agropilotPermissionManagerService = inject(AgropilotPermissionManagerService)
  private readonly user = toSignal(inject(SharedUserService).user, { requireSync: true })

  readonly hasAgropilotPermission = input.required<
    ExpectedPermissions,
    ExpectedPermissions | string[] | string | AgropilotFeature
  >({ transform: toExpectedPermissions })

  private readonly isAllowed = computed(() => {
    const role = this.user()?.agropilotRole
    return Object.entries(this.hasAgropilotPermission()).some(([feature, access]) =>
      this.agropilotPermissionManagerService.hasPermission(role, feature as AgropilotFeature, access),
    )
  })

  constructor() {
    // `isAllowed` only notifies when the boolean flips, so the view is not rebuilt when the user is re-emitted
    effect(() => {
      this.viewContainer.clear()
      if (this.isAllowed()) {
        this.viewContainer.createEmbeddedView(this.templateRef)
      }
    })
  }
}
